import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

const getSourceColor = (sourceName) => {
  const colors = {
    Admob: 'bg-blue-500 hover:bg-blue-600',
    Meta: 'bg-purple-500 hover:bg-purple-600',
    Unity: 'bg-green-500 hover:bg-green-600',
    AppLovin: 'bg-orange-500 hover:bg-orange-600',
    IronSource: 'bg-red-500 hover:bg-red-600',
    Vungle: 'bg-pink-500 hover:bg-pink-600',
  }
  return colors[sourceName] || 'bg-gray-500 hover:bg-gray-600'
}

/**
 * PAM 提交确认对话框
 * 显示预览并要求验证码后才能提交更改
 * 
 * @param {boolean} isSubmitting - 是否正在提交
 * @param {object} submitResult - 提交结果 { success: boolean, message: string }
 */
function PamSubmitConfirmDialog({ 
  isOpen, 
  changedGroups, 
  onConfirm, 
  onCancel,
  isSubmitting = false,
  submitResult = null,
}) {
  const [verificationCode, setVerificationCode] = useState('')
  const [inputCode, setInputCode] = useState('')
  const [error, setError] = useState('')

  // Generate random 6-digit code when dialog opens
  useEffect(() => {
    if (isOpen && !isSubmitting && !submitResult) {
      const code = Math.floor(100000 + Math.random() * 900000).toString()
      setVerificationCode(code)
      setInputCode('')
      setError('')
    }
  }, [isOpen, isSubmitting, submitResult])

  const handleConfirm = () => {
    if (inputCode !== verificationCode) {
      setError('验证码不匹配，请重试')
      return
    }

    setError('')
    onConfirm()
  }

  const handleCancel = () => {
    // 如果正在提交，不允许取消
    if (isSubmitting) {
      return
    }
    
    setInputCode('')
    setError('')
    onCancel()
  }
  
  const handleClose = () => {
    // 只有在不提交状态或已有结果时才允许关闭
    if (!isSubmitting) {
      handleCancel()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isSubmitting ? '正在提交...' : submitResult ? '提交结果' : '确认更改 - 预览'}
          </DialogTitle>
          <DialogDescription>
            {isSubmitting 
              ? '请稍候，正在同步到 AdMob...' 
              : submitResult 
              ? submitResult.success ? '更改已成功提交' : '提交失败'
              : '请查看以下更改并输入验证码以提交'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Loading State */}
          {isSubmitting && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
              <p className="text-lg font-medium text-gray-700">正在同步到 AdMob...</p>
              <p className="text-sm text-gray-500">请勿关闭此窗口</p>
            </div>
          )}

          {/* Submit Result */}
          {submitResult && (
            <Alert variant={submitResult.success ? 'default' : 'destructive'}>
              <AlertDescription className="text-lg">
                {submitResult.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Original Content (hidden during submission) */}
          {!isSubmitting && !submitResult && (
            <>
          {/* Summary */}
          <Alert>
            <AlertDescription>
              Create A/B experiments for <strong>{changedGroups.length}</strong>{' '}
              mediation groups. Variant B receives 50% of users; Variant A keeps
              the existing configuration. Each test covers all linked ad units
              listed below. To test one ad unit, use a group targeting only that unit.
            </AlertDescription>
          </Alert>

          {/* Changes Preview */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">更改预览</h3>
            {changedGroups.map((group, index) => (
              <div
                key={group.id}
                className="border rounded-lg p-4 bg-yellow-50 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold">{group.name}</p>
                    <p className="text-sm text-gray-600">{group.type}</p>
                    <p className="text-sm text-gray-600">{group.platform}</p>
                    <div className="text-xs text-gray-600 mt-2 break-all">
                      <p>Target ad units ({group.adUnitIds?.length ?? 'unknown'}):</p>
                      {(group.adUnitIds || []).map((adUnitId) => (
                        <p key={adUnitId}>{adUnitId}</p>
                      ))}
                    </div>
                  </div>
                    <Badge variant="secondary">已修改</Badge>
                </div>

                {/* Before */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">修改前:</p>
                  <div className="flex flex-wrap gap-1">
                    {group.waterfall.map((item, idx) => (
                      <Badge
                        key={`before-${idx}`}
                        className={`${getSourceColor(item.name)} text-white`}
                      >
                        {item.name}-${item.price}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* After */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">修改后:</p>
                  <div className="flex flex-wrap gap-1">
                    {group.draft.map((item, idx) => (
                      <Badge
                        key={`after-${idx}`}
                        className={`${getSourceColor(item.name)} text-white border-2 border-yellow-400`}
                      >
                        {item.name}-${item.price}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="border-t pt-6 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-red-900">
                ⚠️ 需要验证
              </h3>
              <p className="text-sm text-red-800">
                为防止误操作，请输入以下验证码：
              </p>
              <div className="flex items-center gap-4">
                <div className="bg-white border-2 border-red-300 rounded-lg p-4">
                  <p className="text-3xl font-bold text-red-900 tracking-wider">
                    {verificationCode}
                  </p>
                </div>
                <div className="flex-1">
                  <Input
                    type="text"
                    maxLength={6}
                    value={inputCode}
                    onChange={(e) => {
                      setInputCode(e.target.value)
                      setError('')
                    }}
                    placeholder="输入 6 位验证码"
                    className="text-2xl font-bold text-center tracking-wider"
                  />
                </div>
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>
            </>
          )}
        </div>

        <DialogFooter>
          {isSubmitting ? (
            <Button disabled className="w-full">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              提交中，请稍候...
            </Button>
          ) : submitResult ? (
            <Button 
              variant={submitResult.success ? 'default' : 'destructive'} 
              onClick={handleCancel}
              className="w-full"
            >
              {submitResult.success ? '完成' : '关闭'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleCancel}>
                取消
              </Button>
              <Button
                variant="default"
                onClick={handleConfirm}
                disabled={inputCode.length !== 6}
              >
                确认并提交
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PamSubmitConfirmDialog
